import { useEffect, useState } from 'react';
import type { CardDesignData, CardFormat } from '@flashcard/shared';
import { generateQrDataUrl } from '../../lib/qr';
import { designQrValue } from '../../lib/pdf';

const SIZE: Record<CardFormat, { w: number; h: number }> = {
  standard: { w: 85, h: 55 },
  round: { w: 55, h: 55 },
  square: { w: 55, h: 55 },
};

interface CardCanvasProps {
  design: CardDesignData;
}

export function CardCanvas({ design }: CardCanvasProps) {
  const { w, h } = SIZE[design.format];
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  useEffect(() => {
    const value = designQrValue(design);
    if (value) {
      generateQrDataUrl(value).then(setQrUrl).catch(() => setQrUrl(null));
    } else {
      setQrUrl(null);
    }
  }, [design.qrCode]);

  const radius = design.format === 'round' ? w / 2 : design.format === 'square' ? 0 : 4;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      style={{
        width: '100%',
        maxWidth: '360px',
        aspectRatio: `${w} / ${h}`,
        borderRadius: '8px',
        boxShadow: '0 0 24px rgba(0,0,0,0.4)',
        background: '#1a1a24',
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x={0} y={0} width={w} height={h} rx={radius} fill={design.backgroundColor} />

      {/* Sicherheitsbereich (dezent gestrichelt) */}
      <rect
        x={3}
        y={3}
        width={w - 6}
        height={h - 6}
        rx={radius > 0 ? radius - 3 : 0}
        fill="none"
        stroke="#8b5cf6"
        strokeWidth={0.3}
        strokeDasharray="1.5 1.5"
        opacity={0.5}
      />

      {/* Logo */}
      {design.logoUrl && design.logoBox && (
        <image
          href={design.logoUrl}
          x={design.logoBox.x}
          y={design.logoBox.y}
          width={design.logoBox.width}
          height={design.logoBox.height}
          preserveAspectRatio="xMidYMid meet"
        />
      )}

      {/* Texte */}
      {design.texts.map((t) => {
        const anchor = t.align === 'center' ? 'middle' : t.align === 'right' ? 'end' : 'start';
        const dx = t.align === 'center' ? t.x : t.x; // x ist Ankerpunkt bei middle/end
        return (
          <text
            key={t.id}
            x={dx}
            y={t.y}
            fontSize={t.fontSize}
            fill={t.color}
            fontWeight={t.bold ? 700 : 400}
            textAnchor={anchor}
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {t.content}
          </text>
        );
      })}

      {/* QR-Code */}
      {design.qrCode && qrUrl && (
        <image
          href={qrUrl}
          x={design.qrCode.x}
          y={design.qrCode.y}
          width={design.qrCode.size}
          height={design.qrCode.size}
        />
      )}
    </svg>
  );
}
